import { err, ok } from 'neverthrow';
import { AST } from '../../ast';
import { Markup } from '../../builder';
import { TokenMeta, WithMeta } from '../lex';
import { CompileOutput, ParseError, ParseRes } from '../eraql-ast/ast';
import { c } from './combinators';
import { SpecialForm } from './types';
import _ from 'lodash';

const Text_: SpecialForm = (ast, ctx, func) =>
  c
    .Args(
      c.Expr,
      c.StructOf({
        required: {},
        optional: { modifier: c.EnumLit('none', 'start-case') },
        strict: true,
      })
    )(ast, ctx, func)
    .andThen(([text, opts = {}]) => {
      const mu: AST._Text = {
        t: 'text',
        text: text.ast,
        modifier: opts.modifier ? opts.modifier.v[0] : 'none',
      };

      return tryTypeCheckMu(mu, func[1]);
    });

const Image_: SpecialForm = (ast, ctx, func) =>
  c
    .ArgsWithOpts(
      [c.StringLit] as const,
      c.StructOf({
        required: {},
        optional: {
          description: c.StringLit,
          align: c.EnumLit('left', 'right', 'center'),
          size: c.EnumLit('lg', 'md', 'sm'),
        },
        strict: true,
      })
    )(ast, ctx, func)
    .andThen(([[uri], opts = {}]) => {
      const mu: AST._Image = {
        t: 'image',
        uri: uri,
        description: opts.description ? opts.description.v[0] : null,
        align: opts.align ? opts.align.v[0] : null,
        size: opts.size ? opts.size.v[0] : null,
      };

      return tryTypeCheckMu(mu, func[1]);
    });

const LlmSummary_: SpecialForm = (ast, ctx, func) =>
  c
    .ArgsWithOpts(
      [c.Rel, c.StringLit] as const,
      c.StructOf({
        required: {},
        optional: { size: c.EnumLit('lg', 'md', 'sm') },
        strict: true,
      })
    )(ast, ctx, func)
    .andThen(([[rel, prompt], opts]): ParseRes<CompileOutput> => {
      const size = opts?.size?.v[0] ?? 'md';
      const config: AST.Chart._ChartConfig = {
        t: 'llm-summary',
        prompt,
        // TODO: Handle different models
        model: 'gpt-5-nano',
      };

      const mu: AST._Chart = {
        t: 'chart',
        rel: rel.ast,
        title: null,
        size,
        config,
        format: {},
      };

      return tryTypeCheckMu(mu, func[1]);
    });

const DataGrid_: SpecialForm = (ast, ctx, func) =>
  c
    .ArgsWithOpts(
      [c.Rel] as const,
      c.StructOf({
        required: {},
        optional: {
          title: c.StringLit,
        },
        strict: true,
      })
    )(ast, ctx, func)
    .andThen(([[rel], opts]): ParseRes<CompileOutput> => {
      const title = opts?.title?.v[0];
      const mu = rel.chart.DataGrid({ title });
      return ok({ item: { t: 'mu', mu }, bindings: {} });
    });

const BarChart_: SpecialForm = (args, ctx, func) =>
  c
    .ArgsWithOpts(
      [c.Rel, c.RecordOf(c.Expr)] as const,
      c.StructOf({
        required: {},
        optional: {
          title: c.Expr,
          size: c.EnumLit('lg', 'md', 'sm'),
          type: c.EnumLit('stacked', 'grouped'),
          direction: c.EnumLit('vertical', 'horizontal'),
          trendline: c.BoolLit,
        },
        strict: true,
      })
    )(args, ctx, func)
    .andThen(([[rel, attrs], opts]): ParseRes<CompileOutput> => {
      const config: AST.Chart._ChartConfig = {
        t: 'bar-chart',
        axis: {
          x: { label: null, scale: null },
          y: { label: null, scale: null },
        },
        trendline: opts?.trendline?.v[0] ?? false,
        type: opts?.type?.v[0] ?? 'stacked',
        direction: opts?.direction?.v[0] ?? 'vertical',
      };

      const bc: AST._Chart = {
        t: 'chart',
        rel: rel.select(() => _.mapValues(attrs, (x) => x.v[0])).ast,
        title: opts?.title?.v[0]?.ast ?? null,
        size: opts?.size?.v[0] ?? 'md',
        config,
        format: {},
      };

      return tryTypeCheckMu(bc, func[1]);
    });

const tryTypeCheckMu = (
  mu: AST.Mu,
  meta: TokenMeta
): ParseRes<CompileOutput> => {
  const res = Markup.tryFromAst(mu);

  if (res.isErr()) {
    const e: WithMeta<ParseError> = [
      { t: 'type-check-error', trace: res.error },
      meta,
    ];
    return err([e]);
  }

  return ok({ item: { t: 'mu', mu: res.value }, bindings: {} });
};

export const MU_SPECIAL_FORMS: Record<
  string,
  [arity: number | number[] | 'any', fn: SpecialForm]
> = {
  Image: [[1, 2], Image_],
  Text: [[1, 2], Text_],
  DataGrid: [[1, 2], DataGrid_],
  BarChart: [[2, 3], BarChart_],
  LlmSummary: [[2, 3], LlmSummary_],
};
